import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import { getTemplateFolders } from '../../../api/queries_v2';
import { FolderTreeSelector } from '../../../components/folder-tree-selector';
import PropdocsModal from '../../../components/modal/PropdocsModal';

type MoveFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  teamId: string;
  onSuccess: () => void;
  folderType?: string;
};

const MoveFolderModal = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  teamId,
  onSuccess,
  folderType = 'template',
}: MoveFolderModalProps) => {
  const { formatMessage } = useIntl();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  // Fetch folders of the same type to show as destination options
  const { data: foldersData, loading: foldersLoading } = useQuery(getTemplateFolders as any, {
    variables: { teamId, folderType },
    skip: !isOpen,
  });

  const [updateTemplateFolder] = useMutation(mutations.updateTemplateFolder as any, {
    onCompleted: () => {
      notifySuccess({ message: 'Folder moved successfully' });
      onSuccess();
      onClose();
      setIsLoading(false);
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to move folder' });
      setIsLoading(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      await updateTemplateFolder({
        variables: {
          id: folderId,
          parentFolderId: selectedFolderId, // null for root level
        },
        refetchQueries: [
          {
            query: queriesV2.getTemplateFolders,
            variables: { teamId, folderType },
          },
          {
            query: queriesV2.getDocumentTemplates,
            variables: { teamId, templateType: 'agreement' },
          },
        ],
      });
    } catch (error) {
      console.error('Error moving folder:', error);
      notifyError({ message: 'Failed to move folder' });
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFolderId(null);
    onClose();
  };

  // Filter out the current folder and its children to prevent circular moves
  const availableFolders =
    foldersData?.getTemplateFolders?.filter((folder: any) => {
      // Don't allow moving folder into itself
      if (folder.id === folderId) return false;

      // TODO: Add logic to prevent moving into child folders
      // This would require checking the folder hierarchy

      return true;
    }) || [];

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="lg"
      disableClose={isLoading}
      title={<FormattedMessage id="title.move-folder" defaultMessage="Move Folder" />}
    >
      <Form onSubmit={handleSubmit}>
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <div className="mb-3">
              <strong>
                <FormattedMessage
                  id="message.moving-folder"
                  defaultMessage="Moving: {folderName}"
                  values={{ folderName }}
                />
              </strong>
            </div>

            <Form.Group>
              <Form.Label>
                <FormattedMessage id="label.destination-folder" defaultMessage="Destination Folder" />
              </Form.Label>
              {foldersLoading ? (
                <div className="text-muted">
                  <FormattedMessage id="message.loading-folders" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <FolderTreeSelector
                  folders={availableFolders}
                  selectedFolderId={selectedFolderId}
                  onSelectionChange={setSelectedFolderId}
                  excludeFolderId={folderId}
                  disabled={isLoading}
                  context="templates"
                />
              )}
              <div className="form-text mt-2">
                <FormattedMessage
                  id="folders.move.help"
                  defaultMessage="Choose the destination folder. Select 'All Templates (Root)' to move it to the top level. You cannot move a folder into itself or its children."
                />
              </div>
            </Form.Group>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
            <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isLoading}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading || foldersLoading}>
              {isLoading ? (
                <FormattedMessage id="cta.moving" defaultMessage="Moving..." />
              ) : (
                <TitleFormattedMessage id="cta.move" defaultMessage="Move" />
              )}
            </Button>
          </div>
        </>
      </Form>
    </PropdocsModal>
  );
};

export default MoveFolderModal;
